import { useEffect, useMemo } from 'react'

import { useElara } from '@/providers/elara/hooks/useElara'
import { useElaraActions } from '@/providers/elara/hooks/useElaraActions'
import type { UIAction } from '@/providers/elara/types'

type LocalElaraNavigationActionsProps = {
  selectedMenuItem: string
  onSelectMenuItem: (id: string) => void
  patientMenuItems: Array<{ id: string }>
}

export function LocalElaraNavigationActions({
  selectedMenuItem,
  onSelectMenuItem,
  patientMenuItems,
}: LocalElaraNavigationActionsProps) {
  const { updateUIState } = useElara()!

  useEffect(() => {
    updateUIState('general', { currentSection: selectedMenuItem })
  }, [selectedMenuItem, updateUIState])

  const availableSections = patientMenuItems.map((m) => m.id)

  const elaraActions = useMemo((): UIAction[] => [
    {
      actionId: 'nav.switchSection',
      description: `Switch the active section. Available: ${availableSections.join(', ')}. Current: "${selectedMenuItem}".`,
      section: 'general',
      params: [{
        name: 'section',
        type: 'enum' as const,
        description: 'The section ID to navigate to',
        required: true,
        enumValues: availableSections,
      }],
      handler: async (params) => {
        if (!params?.section || !availableSections.includes(params.section)) {
          return { success: false, message: 'Invalid section' }
        }
        onSelectMenuItem(params.section)
        return { success: true, message: `Switched to ${params.section}` }
      },
    },
    {
      actionId: 'nav.getCurrentSection',
      description: 'Get the currently active section.',
      section: 'general',
      params: [],
      handler: async () => ({ success: true, message: selectedMenuItem, data: { section: selectedMenuItem } }),
    },
  ], [selectedMenuItem, onSelectMenuItem, availableSections])

  useElaraActions(elaraActions)
  return null
}
